import React, { useEffect } from "react"
import { Link, useHistory } from "react-router-dom"
import { CircleImage } from "../CircleImage"
import { NoResultMessage } from "./NoResultMessage"
import { providerFinderRootPath, ProviderFinderParam } from "../../utils/providerFinderSearchUrl"
import { getHeap } from "../../utils/heap"
import { defineMessage, FormattedMessage } from "react-intl"
import { Box, Button, Progress, Typography } from "@carrotfertility/carotene-core"
import { useEnhancedEmptyState } from "#/pages/provider-finder/hooks/useEnhancedEmptyState"
import { ProviderEligibilityMessage } from "#/pages/provider-finder/components/results/ProviderEligibilityMessage"
import { useProviderTypeSearched } from "./useProviderTypeSearched"
import { DoulaNoResultDisplay } from "./DoulaNoResultDisplay"
import icnFind from "../../resources/images/icn-find.png"
import icnFind2x from "../../resources/images/icn-find-2x.png"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { ProviderType } from "#/pages/provider-finder/utils/parentIdMapping"

export function NoResultDisplay({ isCountryCodeNotMatching }: { isCountryCodeNotMatching: boolean }): JSX.Element {
  const { providerTypeSearched } = useProviderTypeSearched()
  const { mutate: trackMemberAction } = useMemberActionTracking()
  const history = useHistory()
  const params = new URLSearchParams(history.location.search)
  const searchLocation = params.get(ProviderFinderParam.Search)
  const keyword = params.get(ProviderFinderParam.Keyword)
  useEffect(
    function trackProviderSearchNoResultsMessageRendered() {
      getHeap().track("Provider Search No Results Message Rendered", {
        "Location Is Outside Country": isCountryCodeNotMatching ? "true" : "false"
      })
    },
    [isCountryCodeNotMatching]
  )

  useEffect(() => {
    trackMemberAction({
      memberActionTaken: MemberActions.PROVIDER_FINDER_EMPTY_STATE_VIEWED,
      metaData: {
        providerType: providerTypeSearched,
        keyword: keyword ?? undefined,
        searchLocation: searchLocation ?? undefined
      }
    })
  }, [providerTypeSearched, keyword, searchLocation, trackMemberAction])

  const enhancedEmptyState = useEnhancedEmptyState()

  if (providerTypeSearched === ProviderType.Doulas) {
    return <DoulaNoResultDisplay isCountryCodeNotMatching={isCountryCodeNotMatching} />
  }

  const headerMessage = enhancedEmptyState.isEnhancedEmptyState
    ? defineMessage({ defaultMessage: "No results in Provider finder, but..." })
    : defineMessage({ defaultMessage: "No results found" })

  const footerMessage = enhancedEmptyState.isEnhancedEmptyState
    ? defineMessage({ defaultMessage: "Search again" })
    : defineMessage({ defaultMessage: "Reset location" })

  return enhancedEmptyState.isLoadingContent ? (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Progress />
    </Box>
  ) : (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
      padding="80px 0 0 0"
      gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
    >
      <CircleImage src={icnFind} srcSet={icnFind2x} backgroundColor="lightBlue" size={84} alt="" />
      <Box textAlign="center">
        <Typography variant="h4" color="secondary">
          <FormattedMessage {...headerMessage} />
        </Typography>
        {enhancedEmptyState.isEnhancedEmptyState ? (
          <ProviderEligibilityMessage
            displayName={enhancedEmptyState.emptyStateProviderTypeDisplayName}
            dialogContent={enhancedEmptyState.emptyStateContent}
          />
        ) : (
          <NoResultMessage isCountryCodeNotMatching={isCountryCodeNotMatching} />
        )}
      </Box>
      <Link to={providerFinderRootPath}>
        <Button variant="outlined" color="secondary" size="medium">
          <FormattedMessage {...footerMessage} />
        </Button>
      </Link>
    </Box>
  )
}
